import { DISCOUNT_TYPES } from './promoCode.consts'
import { type Coupon, type GiftCardCoupon } from './promoCode.types'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { TILE_SIZES } from '@mixtiles/web-backend-shared'

export function getGivenDiscountCouponType(coupon: Coupon | null = null) {
  // On Voucherify there is only 3 types of discount coupons - UNIT, PERCENTS or AMOUNT
  // in order to expend the discount types we add "discountType" field on voucher.metadata
  // this method purpose is to prevent other developers to count on voucher.type which is limited
  if (
    coupon &&
    ([DISCOUNT_TYPES.PERCENT, DISCOUNT_TYPES.UNIT] as string[]).includes(
      coupon?.discount?.type || ''
    )
  ) {
    return coupon.discount?.type || ''
  } else if (coupon?.metadata?.discountType) {
    return coupon.metadata.discountType
  }
  return null
}

// The photo book page checks out through the shared bag. The backend applies a
// tiles promo to the whole bag once it holds tiles, books included, and skips
// it for a bag of books only.
export function getProductTypeForCoupon(
  coupon: Coupon,
  {
    pageProductType,
    hasTilesInBag,
  }: { pageProductType: PRODUCT_TYPES; hasTilesInBag: boolean }
) {
  if (
    pageProductType === PRODUCT_TYPES.PHOTO_BOOK &&
    coupon.metadata?.productType === PRODUCT_TYPES.CLASSIC &&
    hasTilesInBag
  ) {
    return PRODUCT_TYPES.CLASSIC
  }
  return pageProductType
}

export function isValidForProductType(
  discountCoupon: Coupon,
  productType: PRODUCT_TYPES
) {
  if (
    productType === PRODUCT_TYPES.PHOTO_BOOK &&
    !(
      [
        DISCOUNT_TYPES.PERCENT,
        DISCOUNT_TYPES.BUYOVER,
        DISCOUNT_TYPES.BUNDLE,
        DISCOUNT_TYPES.MIX,
      ] as string[]
    ).includes(getGivenDiscountCouponType(discountCoupon) || '')
  ) {
    return false
  }
  if (discountCoupon?.metadata?.productType) {
    if (
      productType === PRODUCT_TYPES.GIFT_CARD &&
      discountCoupon?.metadata?.productType === PRODUCT_TYPES.CLASSIC
    ) {
      // since tiles gift cards are only classic 8X8
      const coupon = discountCoupon as GiftCardCoupon
      return (
        !coupon.metadata.tileSizesList ||
        coupon.metadata.tileSizesList?.includes(TILE_SIZES.SQUARE_8X8)
      )
    } else {
      return discountCoupon.metadata.productType === productType
    }
  } else {
    return true
  }
}

/**
 * Checks if a gift card is a book gift card based on tile sizes list.
 * Book gift cards have sizes that start with 'BOOK_' prefix.
 *
 * @param tileSizesList - Array of tile size strings (e.g., ['BOOK_8X8', 'BOOK_10X10'])
 * @returns true if any size in the list starts with 'BOOK_', false otherwise
 */
export function isBookGiftCard(tileSizesList: string[] | undefined): boolean {
  return (
    Array.isArray(tileSizesList) &&
    tileSizesList.some((size) => size.startsWith('BOOK_'))
  )
}
